import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { articles, getRecentArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { faqs } from '@/mocks/faqs';

function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  type = 'WebPage',
  jsonLd,
}: {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  type?: string;
  jsonLd?: Record<string, unknown>;
}) {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', keywords);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', canonicalUrl);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', description);

    if (jsonLd) {
      const existingScript = document.getElementById('page-jsonld');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = 'page-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, jsonLd]);

  return null;
}

function ServiceCard({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div className="group p-8 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-5 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
        <i className={`${icon} text-xl`}></i>
      </div>
      <h3 className="text-lg font-semibold text-foreground-900 mb-2 font-heading">
        {title}
      </h3>
      <p className="text-sm text-foreground-500 leading-relaxed">{description}</p>
    </div>
  );
}

function StatItem({ value, label, suffix }: { value: string; label: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const target = parseInt(value, 10);
  const ref = useRef<HTMLDivElement>(null);
  const counted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !counted.current) {
          counted.current = true;
          const duration = 2000;
          const startTime = performance.now();
          const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-2">
        {count.toLocaleString()}
        {suffix && <span className="text-primary-500">{suffix}</span>}
      </div>
      <div className="text-sm text-foreground-500">{label}</div>
    </div>
  );
}

function ArticleCard({ article }: { article: (typeof articles)[0] }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 overflow-hidden cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={`${article.title} - 问鼎.cc`}
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">
            {article.category === 'brand-news' ? '品牌动态' :
             article.category === 'industry-insight' ? '行业洞察' :
             article.category === 'product-service' ? '产品服务' :
             article.category === 'tech-frontier' ? '技术前沿' : '客户案例'}
          </span>
          <span className="text-xs text-foreground-400">{article.date}</span>
          <span className="text-xs text-foreground-400">{article.readTime}</span>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 font-heading">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
      </div>
    </Link>
  );
}

function FaqItem({ faq, isOpen, onToggle }: { faq: (typeof faqs)[0]; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border border-background-200/70 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-background-50 transition-colors"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium text-foreground-800 pr-4 font-heading">{faq.question}</span>
        <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 w-5 h-5 flex items-center justify-center ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      {isOpen && (
        <div className="px-6 pb-5">
          <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const recentArticles = getRecentArticles(4);

  const homepageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '问鼎.cc — 官方直达入口',
    description: '问鼎.cc 是您信赖的品牌官方平台，提供专业服务与优质体验。快速直达问鼎.cc，享受便捷注册、安全保障与全方位支持。',
    url: 'https://hecaoqing.com.cn/',
    isPartOf: {
      '@type': 'WebSite',
      name: '问鼎.cc',
      url: 'https://hecaoqing.com.cn/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hecaoqing.com.cn/' },
      ],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SeoHead
        title="问鼎.cc — 官方直达入口 | 问鼎cc官网 问鼎cc入口"
        description="问鼎.cc 是您信赖的品牌官方平台，提供专业服务与优质体验。快速直达问鼎.cc，享受便捷注册、安全保障与全方位支持，立即访问问鼎.cc 开启您的精彩旅程。"
        keywords="问鼎.cc,问鼎cc官网,问鼎cc入口,问鼎cc注册,问鼎cc登录,问鼎cc最新地址,问鼎cc官方平台"
        canonicalUrl="https://hecaoqing.com.cn/"
        jsonLd={homepageJsonLd}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[680px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20with%20flowing%20golden%20and%20warm%20coral%20gradients%2C%20soft%20layered%20shapes%20creating%20depth%2C%20luxurious%20minimalist%20background%20with%20subtle%20texture%2C%20warm%20cream%20and%20beige%20tones%2C%20professional%20corporate%20atmosphere%2C%20no%20text%2C%20clean%20elegant%20aesthetic&width=1800&height=800&seq=hero-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center py-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs mb-8">
              <i className="ri-verified-badge-line w-4 h-4 flex items-center justify-center"></i>
              <span>官方认证平台 · 安全可信赖</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-white mb-6 leading-tight tracking-tight">
              问鼎<span className="text-primary-400">.cc</span>
              <br />
              <span className="text-2xl md:text-4xl lg:text-5xl font-normal opacity-90">
                品牌官方直达入口
              </span>
            </h1>

            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
              专业的品牌官方平台，为您提供安全、稳定、高效的一站式品牌数字化服务。
              立即访问问鼎.cc，开启您的品牌数字化之旅。
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
              >
                立即注册
              </Link>
              <Link
                to="/category/brand-news"
                className="px-8 py-3.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold text-sm hover:bg-white/20 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                核心服务
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm leading-relaxed">
                问鼎.cc 为您提供全方位的品牌数字化解决方案，助力品牌在数字世界脱颖而出
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <ServiceCard
                icon="ri-shield-check-line"
                title="安全防护"
                description="多层安全体系，SSL加密、DDoS防护、WAF防火墙，全方位保障品牌数字资产安全。"
              />
              <ServiceCard
                icon="ri-global-line"
                title="品牌官网"
                description="专业品牌官网建设，极速加载、多端适配，完美展现品牌形象与价值。"
              />
              <ServiceCard
                icon="ri-bar-chart-box-line"
                title="数据分析"
                description="实时数据监控与深度分析，精准洞察用户行为，驱动品牌决策优化。"
              />
              <ServiceCard
                icon="ri-customer-service-2-line"
                title="专属支持"
                description="7×24小时专业客服团队，快速响应需求，为您提供定制化解决方案。"
              />
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
              <StatItem value="50000" suffix="+" label="服务用户数" />
              <StatItem value="999" suffix="%" label="平台可用性" />
              <StatItem value="200" suffix="+" label="合作企业" />
              <StatItem value="50" suffix="+" label="覆盖城市" />
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                内容导航
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm leading-relaxed">
                探索不同内容分类，获取品牌建设、行业洞察、技术前沿等专业资讯
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center text-center p-8 rounded-2xl bg-background-50 border border-background-200/70 hover:border-accent-300 hover:bg-accent-50 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-50 text-accent-500 mb-4 group-hover:bg-accent-500 group-hover:text-white transition-all duration-300">
                    <i className={`${cat.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-400 leading-relaxed line-clamp-2 mb-3">
                    {cat.description}
                  </p>
                  <span className="text-xs text-accent-500 font-medium">
                    {cat.count} 篇文章
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Recent Articles */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-14 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                  最新资讯
                </h2>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  第一时间了解问鼎.cc 的最新动态与行业洞察
                </p>
              </div>
              <Link
                to="/category/brand-news"
                className="flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm leading-relaxed">
                关于问鼎.cc，您可能想了解的问题
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  faq={faq}
                  isOpen={openFaq === index}
                  onToggle={() => setOpenFaq(openFaq === index ? null : index)}
                />
              ))}
            </div>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20golden%20and%20coral%20gradient%20abstract%20background%20with%20soft%20flowing%20geometric%20shapes%2C%20luxurious%20elegant%20texture%2C%20minimalist%20style%2C%20warm%20inviting%20atmosphere%2C%20professional%20business%20aesthetic&width=1600&height=500&seq=cta-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
          </div>

          <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mb-6">
              准备好开启您的品牌数字化之旅了吗？
            </h2>
            <p className="text-white/80 text-sm mb-10 max-w-lg mx-auto leading-relaxed">
              立即注册问鼎.cc，享受专业、安全、高效的品牌官方平台服务
            </p>
            <Link
              to="/about"
              className="inline-flex px-8 py-3.5 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
            >
              立即注册
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}